import { c as _c } from "react/compiler-runtime";
import { IcoBolt16, IcoCopy16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { LinkButton, useConfirmationDialog } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import InlineEditableText from '@/components/inline-editable-text';
import { useNodeCollapse } from '../../hooks/use-canvas-collapse';
import { useNodeSave } from '../../hooks/use-node-save';
import { questionKeyError } from '../../store/field-key';
import { useBuilder } from '../../store/provider';
import type { DecisionNode } from '../../store/types';
import { uid } from '../../store/utils';
import MoveControls from '../move-controls';
import { PlanBlock } from '../plan-block';
import { ClickToReveal, ProsePreview } from '../shared/inline-edit';
import DecisionNodeSuggestions from '../suggestion/decision-suggestions';
import MarkdownField from '../task/markdown-field';
import ValidationAlerts from '../validation-alerts';
import QuestionCard from './question-card';
import StateRefs from './state-refs';
type DecisionBlockProps = {
  node: DecisionNode;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
};
const DecisionBlock = t0 => {
  const $ = _c(87);
  const {
    node,
    canMoveUp,
    canMoveDown
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.decision"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly;
  const isHighlighted = state.highlightedId === `decision-${node.id}`;
  const confirm = useConfirmationDialog();
  const t2 = node.id;
  let t3;
  if ($[1] !== t) {
    t3 = t(_temp);
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t) {
    t4 = t(_temp2);
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const collapseProps = useNodeCollapse(t2, t3, t4);
  const showMoveControls = canMoveUp || canMoveDown;
  const save = useNodeSave(node.id);
  let t5;
  if ($[5] !== actions || $[6] !== confirm || $[7] !== node.id || $[8] !== node.name || $[9] !== t) {
    t5 = () => {
      confirm.open({
        title: t(_temp3),
        description: t(_temp4, {
          name: node.name || t(_temp5)
        }),
        variant: "destructive",
        primaryButton: {
          label: t(_temp6),
          onClick: () => actions.removeNode(node.id)
        },
        secondaryButton: {
          label: t(_temp7)
        }
      });
    };
    $[5] = actions;
    $[6] = confirm;
    $[7] = node.id;
    $[8] = node.name;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleDelete = t5;
  const t6 = `decision-${node.id}`;
  const t7 = node.name;
  let t8;
  if ($[11] !== t) {
    t8 = t(_temp8);
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t(_temp9);
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t(_temp10);
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = state.focusNodeId === node.id;
  let t12;
  if ($[17] !== actions.decision || $[18] !== node.id) {
    t12 = name => actions.decision.updateNode(node.id, {
      name
    });
    $[17] = actions.decision;
    $[18] = node.id;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== canEdit || $[21] !== node.name || $[22] !== t10 || $[23] !== t11 || $[24] !== t12 || $[25] !== t8 || $[26] !== t9) {
    t13 = <InlineEditableText value={t7} fallback={t8} placeholder={t9} ariaLabel={t10} canEdit={canEdit} autoFocus={t11} onChange={t12} />;
    $[20] = canEdit;
    $[21] = node.name;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t8;
    $[26] = t9;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <IcoBolt16 color="tertiary" />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t) {
    t15 = t(_temp11);
    $[29] = t;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = `@${node.id}`;
  let t17;
  if ($[31] !== actions || $[32] !== canEdit || $[33] !== canMoveDown || $[34] !== canMoveUp || $[35] !== handleDelete || $[36] !== node || $[37] !== showMoveControls || $[38] !== t) {
    t17 = canEdit && <>{showMoveControls && <MoveControls canUp={!!canMoveUp} canDown={!!canMoveDown} onMove={direction => actions.moveNode(node.id, direction)} />}<button type="button" aria-label={t(_temp12)} onClick={e => {
        e.stopPropagation();
        actions.copyNode(node);
      }} className="w-6 h-6 max-md:size-8 rounded-(--icon-button-border-radius-tiny) max-md:rounded-(--icon-button-border-radius) flex items-center justify-center cursor-pointer hover:bg-hover-control"><IcoCopy16 color="tertiary" /></button><button type="button" aria-label={t(_temp13)} onClick={e_0 => {
        e_0.stopPropagation();
        handleDelete();
      }} className="w-6 h-6 max-md:size-8 rounded-(--icon-button-border-radius-tiny) max-md:rounded-(--icon-button-border-radius) flex items-center justify-center cursor-pointer hover:bg-hover-control"><IcoTrash16 color="tertiary" /></button></>;
    $[31] = actions;
    $[32] = canEdit;
    $[33] = canMoveDown;
    $[34] = canMoveUp;
    $[35] = handleDelete;
    $[36] = node;
    $[37] = showMoveControls;
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== node.id) {
    t18 = <ValidationAlerts nodeId={node.id} />;
    $[40] = node.id;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t(_temp14);
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const t20 = node.context;
  let t21;
  if ($[44] !== t) {
    t21 = t(_temp15);
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== canEdit || $[47] !== node.context || $[48] !== t21) {
    t22 = <ProsePreview value={t20} placeholder={t21} canEdit={canEdit} size="large" />;
    $[46] = canEdit;
    $[47] = node.context;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== actions.decision || $[51] !== node.context || $[52] !== node.id || $[53] !== t) {
    t23 = () => <MarkdownField label={t(_temp16)} hint={t(_temp17)} value={node.context} onChange={context => actions.decision.updateNode(node.id, {
      context
    })} mentionNodeId={node.id} />;
    $[50] = actions.decision;
    $[51] = node.context;
    $[52] = node.id;
    $[53] = t;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== canEdit || $[56] !== t19 || $[57] !== t22 || $[58] !== t23) {
    t24 = <ClickToReveal canEdit={canEdit} ariaLabel={t19} className="w-full -mx-1 px-1 py-1" display={t22}>{t23}</ClickToReveal>;
    $[55] = canEdit;
    $[56] = t19;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== node) {
    t25 = <StateRefs node={node} />;
    $[60] = node;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== node.id || $[63] !== node.questions) {
    t26 = node.questions.length > 0 && <div className="flex flex-col gap-2">{node.questions.map((question, index) => <QuestionCard key={question.id} nodeId={node.id} question={question} keyError={questionKeyError(question, node.questions)} canUp={index > 0} canDown={index < node.questions.length - 1} />)}</div>;
    $[62] = node.id;
    $[63] = node.questions;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== actions.decision || $[66] !== canEdit || $[67] !== node.id || $[68] !== t) {
    t27 = canEdit && <div className="flex items-center gap-4"><LinkButton variant="caption-3" iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => actions.decision.addQuestion(node.id, "yes_no", uid("q"))}>{t(_temp18)}</LinkButton><LinkButton variant="caption-3" iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => actions.decision.addQuestion(node.id, "pick_one", uid("q"))}>{t(_temp19)}</LinkButton><LinkButton variant="caption-3" iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => actions.decision.addQuestion(node.id, "score", uid("q"))}>{t(_temp20)}</LinkButton></div>;
    $[65] = actions.decision;
    $[66] = canEdit;
    $[67] = node.id;
    $[68] = t;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== node) {
    t28 = <DecisionNodeSuggestions node={node} />;
    $[70] = node;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== collapseProps || $[73] !== isHighlighted || $[74] !== save || $[75] !== t13 || $[76] !== t15 || $[77] !== t16 || $[78] !== t17 || $[79] !== t18 || $[80] !== t24 || $[81] !== t25 || $[82] !== t26 || $[83] !== t27 || $[84] !== t28 || $[85] !== t6) {
    t29 = <PlanBlock onBlur={save} id={t6} {...collapseProps} headerTitle={t13} isHighlighted={isHighlighted} footerIcon={t14} footerTitle={t15} footerRef={t16} contentClassName="gap-4" headerActions={t17}>{t18}{t24}{t25}{t26}{t27}{t28}</PlanBlock>;
    $[72] = collapseProps;
    $[73] = isHighlighted;
    $[74] = save;
    $[75] = t13;
    $[76] = t15;
    $[77] = t16;
    $[78] = t17;
    $[79] = t18;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
    $[85] = t6;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  return t29;
};
export default DecisionBlock;
function _temp($) {
  return $.expand;
}
function _temp2($_0) {
  return $_0.collapse;
}
function _temp3($_1) {
  return $_1.delete.title;
}
function _temp4($_2) {
  return $_2.delete.description;
}
function _temp5($_3) {
  return $_3.newDecisionNode;
}
function _temp6($_4) {
  return $_4.delete.confirm;
}
function _temp7($_5) {
  return $_5.delete.cancel;
}
function _temp8($_6) {
  return $_6.newDecisionNode;
}
function _temp9($_7) {
  return $_7.name.placeholder;
}
function _temp10($_8) {
  return $_8.name.label;
}
function _temp11($_9) {
  return $_9.title;
}
function _temp12($_10) {
  return $_10.copy.ariaLabel;
}
function _temp13($_11) {
  return $_11.delete.ariaLabel;
}
function _temp14($_12) {
  return $_12.context.ariaLabel;
}
function _temp15($_13) {
  return $_13.context.placeholder;
}
function _temp16($_14) {
  return $_14.context.label;
}
function _temp17($_15) {
  return $_15.context.hint;
}
function _temp18($_16) {
  return $_16.addYesNo;
}
function _temp19($_17) {
  return $_17.addPickOne;
}
function _temp20($_18) {
  return $_18.addScore;
}